<template>
  <div>
    <queclink-tracker-config-filter
      :filters.sync="filters"
      :categories="categories"
      @clear="clearFilters"
    />
    <o-table
      :items="filteredTrackers"
      :loading="loading"
      :draggable="false"
      :checkable="true"
      :sticky-header="true"
      :is-row-checked="isRowChecked"
      :checked-rows="checkedQueclinkTrackerConfigs"
      @check="$emit('update:checkedQueclinkTrackerConfigs', $event)"
    >
      <b-table-column v-slot="{ row }" field="trackerNumber" label="Balise" sortable>
        <router-link
          :to="{
            name: 'queclinkTracker.edit',
            params: { id: row.id },
          }"
        >
          {{ row.trackerNumber }}
        </router-link>
      </b-table-column>
      <b-table-column v-slot="{ row }" field="vehicleNumber" label="Dossard" sortable>
        {{ row.vehicleNumber }}
      </b-table-column>
      <b-table-column v-slot="{ row }" field="categoryName" label="Catégorie" sortable>
        {{ row.categoryName }}
      </b-table-column>
      <b-table-column
        v-slot="{ row }"
        field="performanceIntervalSeconds"
        label="Intervalle performance"
        sortable
      >
        <p>
          <span class="is-size-7 has-text-grey mr-1">Point</span>
          <b-tooltip
            v-if="row.performanceIntervalSeconds > row.defaultIntervalSeconds"
            position="is-top"
            multilined
            type="is-light"
          >
            <span class="has-text-danger">{{ row.performanceIntervalSeconds }} s</span>
            <b-icon icon="circle-info" size="is-small" type="is-danger" />
            <template v-slot:content>L'intervalle est supérieur à l'intervalle défaut.</template>
          </b-tooltip>
          <template v-else>{{ row.performanceIntervalSeconds }} s</template>
        </p>
        <p v-if="row.performancePointsPerMessage > MIN_POINTS_PER_MESSAGE">
          <span class="is-size-7 has-text-grey mr-1">Envoi</span>
          {{ formatDuration(row.performanceSendIntervalSeconds) }}
        </p>
      </b-table-column>
      <b-table-column
        v-slot="{ row }"
        field="defaultIntervalSeconds"
        label="Intervalle défaut"
        sortable
      >
        <p>
          <span class="is-size-7 has-text-grey mr-1">Point</span>
          <b-tooltip
            v-if="
              row.defaultIntervalSeconds < row.performanceIntervalSeconds ||
              row.defaultIntervalSeconds > row.idleIntervalSeconds
            "
            position="is-top"
            multilined
            type="is-light"
          >
            <span class="has-text-danger">{{ row.defaultIntervalSeconds }} s</span>
            <b-icon icon="circle-info" size="is-small" type="is-danger" />
            <template v-slot:content>
              {{
                row.defaultIntervalSeconds > row.idleIntervalSeconds
                  ? "L'intervalle est supérieur à l'intervalle repos."
                  : "L'intervalle est inférieur à l'intervalle performance."
              }}
            </template>
          </b-tooltip>
          <template v-else>{{ row.defaultIntervalSeconds }} s</template>
        </p>
        <p v-if="row.defaultPointsPerMessage > MIN_POINTS_PER_MESSAGE">
          <span class="is-size-7 has-text-grey mr-1">Envoi</span>
          {{ formatDuration(row.defaultSendIntervalSeconds) }}
        </p>
      </b-table-column>
      <b-table-column
        v-slot="{ row }"
        field="idleIntervalSeconds"
        label="Intervalle repos"
        sortable
      >
        <p>
          <span class="is-size-7 has-text-grey mr-1">Point</span>
          <b-tooltip
            v-if="row.idleIntervalSeconds < row.defaultIntervalSeconds"
            position="is-top"
            multilined
            type="is-light"
          >
            <span class="has-text-danger">{{ row.idleIntervalSeconds }} s</span>
            <b-icon icon="circle-info" size="is-small" type="is-danger" />
            <template v-slot:content>L'intervalle est inférieur à l'intervalle défaut.</template>
          </b-tooltip>
          <template v-else>{{ row.idleIntervalSeconds }} s</template>
        </p>
      </b-table-column>
      <b-table-column v-slot="{ row }" field="sleepEnabled" label="Veille" sortable>
        <b-tag v-if="row.sleepEnabled">ON</b-tag>
        <b-tag v-else type="is-danger">OFF</b-tag>
      </b-table-column>
      <b-table-column v-slot="{ row }" field="initializedAt" label="Initialisé" sortable>
        <template v-if="row.isInitialized">
          <b-icon size="is-small" type="is-success" icon="check" />
          {{ format(row.initializedAt) }}
        </template>
        <template v-else>
          <b-icon size="is-small" type="is-danger" icon="times" />
        </template>
      </b-table-column>
      <b-table-column v-slot="{ row }" field="synchronizedAt" label="Synchronisé" sortable>
        <template v-if="row.isProcessing">
          <b-icon size="is-small" type="is-info" icon="hourglass-half" />
          {{ format(row.synchronizedAt) }}
        </template>
        <template v-else-if="row.synchronizedAt">
          <div>
            <b-icon size="is-small" type="is-success" icon="check" />
            {{ format(row.synchronizedAt) }}
          </div>
          <b-tooltip
            v-if="row.expectedNextSynchronizedAt"
            label="Prochaine synchronisation (estimation)"
            position="is-top"
          >
            <b-icon size="is-small" type="is-info" icon="sync" />
            {{ format(row.expectedNextSynchronizedAt) }}
          </b-tooltip>
        </template>
        <template v-else>
          <b-icon size="is-small" type="is-danger" icon="times" />
        </template>
      </b-table-column>
      <b-table-column v-slot="{ row }" field="syncStatus" label="Status" sortable>
        {{ getStatus(row.syncStatus) }}
      </b-table-column>
    </o-table>
  </div>
</template>

<script>
import { QUECLINK_TRACKER_CONFIG_SYNC_STATUS } from '@constants/queclinkTrackerConfig/syncStatus'
import QueclinkTrackerConfigFilter from './QueclinkTrackerConfigFilter.vue'
import { QUECLINK_TRACKER_TYPE } from '@constants/tracker/type'
import { MIN_POINTS_PER_MESSAGE } from '@constants/queclinkTrackerConfig/pointsPerMessage'
import OTable from '@components/Table.vue'
import { format } from 'date-fns'
import { formatDuration } from '@helpers/formatDuration'

export default {
  name: 'QueclinkTrackerConfigTable',

  components: {
    OTable,
    QueclinkTrackerConfigFilter,
  },

  props: {
    loading: {
      type: Boolean,
      required: true,
    },
    liveVehicles: {
      type: Array,
      required: true,
    },
    queclinkTrackerConfigs: {
      type: Array,
      required: true,
    },
    checkedQueclinkTrackerConfigs: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      MIN_POINTS_PER_MESSAGE,
      filters: {
        liveCategoryId: null,
      },
    }
  },

  watch: {
    filters: {
      handler() {
        // reset checked rows on filter change
        this.$emit('update:checkedQueclinkTrackerConfigs', [])
      },
      deep: true,
    },
  },

  computed: {
    trackers() {
      const queclinkTrackerConfigs = this.queclinkTrackerConfigs.reduce(
        (configs, config) => ({ ...configs, [config.id]: config }),
        {},
      )

      const trackers = []
      for (const vehicle of this.liveVehicles) {
        if (vehicle.trackers.length === 0) {
          continue
        }

        for (const tracker of vehicle.trackers) {
          if (tracker.type !== QUECLINK_TRACKER_TYPE) {
            continue
          }

          trackers.push({
            ...queclinkTrackerConfigs[tracker.id],
            trackerNumber: tracker.number,
            vehicleNumber: vehicle.number,
            categoryName: vehicle.categoryName,
            liveCategoryId: vehicle.liveCategoryId,
          })
        }
      }

      return trackers
    },

    // Extracts unique categories to populate the filter
    categories() {
      return Object.values(
        this.trackers.reduce((categories, tracker) => {
          return {
            ...categories,
            [tracker.liveCategoryId]: {
              id: tracker.liveCategoryId,
              name: tracker.categoryName,
            },
          }
        }, {}),
      )
    },

    filteredTrackers() {
      return this.trackers.filter((tracker) =>
        this.filters.liveCategoryId ? this.filters.liveCategoryId === tracker.liveCategoryId : true,
      )
    },
  },

  methods: {
    getStatus(status) {
      const syncStatus = QUECLINK_TRACKER_CONFIG_SYNC_STATUS[status]

      return syncStatus ?? 'Aucune configuration'
    },

    format(date) {
      return format(date, 'dd/MM/yyyy HH:mm')
    },

    formatDuration,

    // prevent auto reset checked row on update rows (from Buefy)
    isRowChecked(checkedRow, row) {
      return checkedRow.id === row.id
    },

    clearFilters() {
      for (const filter in this.filters) {
        this.filters[filter] = null
      }
    },
  },
}
</script>
